/**
 * FETCH DEDUPLICATION + CACHING LAYER (ENTERPRISE AUTH)
 *
 * Solves two problems that cause cascading HTTP waterfalls:
 *
 * 1. DEDUPLICATION: When multiple components fire the same GET request
 *    simultaneously, only ONE actual fetch() goes to the network.
 *
 * 2. SHORT-TERM CACHING: Completed responses are cached for TTL ms.
 *
 * ENTERPRISE AUTH (Feb 26, 2026):
 * - ALWAYS attaches Authorization: Bearer <sessionToken> for authenticated requests
 * - Reads token from localStorage (pathkit_session_token) automatically
 * - NO custom headers (x-session-token removed)
 * - NO cookies
 */

import { getStoredSessionToken, SESSION_TOKEN_KEY } from "./apiHeaders";
import { publicAnonKey } from "./supabase/info";

interface CacheEntry {
  response: Response;
  body: string;
  status: number;
  headers: Record<string, string>;
  expiresAt: number;
}

const inflight = new Map<string, Promise<CacheEntry>>();
const cache = new Map<string, CacheEntry>();

const DEFAULT_TTL_MS = 30_000;
const MAX_CACHE_SIZE = 100;

// ---------- helpers ----------

function isSupabaseFunctionsUrl(url: string): boolean {
  // Works for both absolute and relative URLs.
  // Your calls look like: https://<ref>.supabase.co/functions/v1/<fn>/...
  return url.includes("/functions/v1/");
}

function toHeaders(init?: RequestInit): Headers {
  const h = new Headers();
  const src = init?.headers;

  if (!src) return h;

  if (src instanceof Headers) {
    src.forEach((v, k) => h.set(k, v));
    return h;
  }

  // Record<string, string> or [][]
  try {
    // @ts-ignore
    for (const [k, v] of Object.entries(src)) {
      if (typeof v !== "undefined") h.set(k, String(v));
    }
  } catch {
    // ignore
  }
  return h;
}

function getBearerToken(headers: Headers): string | null {
  const auth = headers.get("Authorization") || headers.get("authorization");
  if (!auth) return null;
  const m = auth.match(/^Bearer\s+(.+)$/i);
  return m?.[1]?.trim() || null;
}

/**
 * ENTERPRISE AUTH: Attach Authorization: Bearer header with session token
 * 
 * CRITICAL FIX: This function ALWAYS reads token from localStorage
 * and attaches it to the request for Supabase Edge Functions.
 */
function setSupabaseAuthHeaders(url: string, init?: RequestInit): RequestInit | undefined {
  if (!isSupabaseFunctionsUrl(url)) return init;

  const headers = toHeaders(init);

  // CRITICAL: Auto-load session token from localStorage if not already in headers
  let sessionToken = getBearerToken(headers);
  
  // If no token in headers OR if it's the anon key, try to load from localStorage
  if (!sessionToken || sessionToken === publicAnonKey) {
    const storedToken = getStoredSessionToken();
    if (storedToken) {
      sessionToken = storedToken;
      console.log(`[cachedFetch] Auto-loaded session token from localStorage (length: ${storedToken.length})`);
    }
  }

  // Use session token if available, otherwise use anon key
  const authToken = sessionToken || publicAnonKey;
  const isSessionToken = sessionToken && (sessionToken.startsWith('sess_') || sessionToken.startsWith('imp_'));
  
  // DIAGNOSTIC LOG
  console.log(`[cachedFetch] Setting auth headers: authAttached=${isSessionToken}, tokenSource=${
    isSessionToken ? 'localStorage' : 'anonKey'
  }`);

  headers.set("Authorization", `Bearer ${authToken}`);
  headers.set("apikey", publicAnonKey);

  return {
    ...(init || {}),
    headers,
  };
}

function cacheKey(url: string, init?: RequestInit): string {
  const method = init?.method?.toUpperCase() || "GET";
  if (method !== "GET") return "";

  // Include session token in cache key so different users get different cache entries
  const headers = toHeaders(init);
  const bearer = getBearerToken(headers);
  
  // If bearer is session token (not anon key), use last 12 chars for cache key
  const sessionKey = bearer && bearer !== publicAnonKey ? bearer.slice(-12) : "";
  
  return `${url}|session:${sessionKey}`;
}

function pruneIfNeeded(): void {
  if (cache.size <= MAX_CACHE_SIZE) return;
  const now = Date.now();
  for (const [k, v] of cache) {
    if (v.expiresAt <= now) cache.delete(k);
  }
  if (cache.size > MAX_CACHE_SIZE) {
    const entries = [...cache.entries()];
    entries.sort((a, b) => a[1].expiresAt - b[1].expiresAt);
    const toDelete = entries.slice(0, entries.length - MAX_CACHE_SIZE);
    toDelete.forEach(([k]) => cache.delete(k));
  }
}

function reconstructResponse(entry: CacheEntry): Response {
  return new Response(entry.body, {
    status: entry.status,
    headers: entry.headers,
  });
}

// ---------- exports ----------

/**
 * ENTERPRISE AUTH: Always attaches session token from localStorage
 * 
 * CRITICAL FIX: This function now ALWAYS reads from localStorage
 * and attaches Authorization: Bearer <sessionToken> automatically.
 */
export async function cachedFetch(
  url: string,
  init?: RequestInit,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<Response> {
  // Always normalize auth headers for Supabase function calls (GET + non-GET)
  const normalizedInit = setSupabaseAuthHeaders(url, init);

  // DIAGNOSTIC: Log all Supabase function calls
  if (isSupabaseFunctionsUrl(url)) {
    const h = toHeaders(normalizedInit);
    const bearer = getBearerToken(h);
    const hasSession = bearer && bearer !== publicAnonKey;
    const tokenInfo = hasSession ? `Token: ${bearer?.slice(0, 10)}...` : "Token: ANON_KEY";
    
    console.log("[cachedFetch] Supabase function call:", {
      url: url.split("/functions/v1/")[1] || url,
      method: (normalizedInit?.method || "GET").toUpperCase(),
      authAttached: hasSession,
      tokenInfo,
    });
  }

  
  const key = cacheKey(url, normalizedInit);

  // Non-cacheable request (POST/PUT/DELETE/etc.)
  if (!key) {
    return fetch(url, normalizedInit);
  }

  // 1) completed cache
  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    console.log(`[cachedFetch] Cache HIT for ${url}`);
    return reconstructResponse(cached);
  }
  if (cached) cache.delete(key);

  // 2) in-flight dedup
  const existing = inflight.get(key);
  if (existing) {
    console.log(`[cachedFetch] In-flight dedup for ${url}`);
    const entry = await existing;
    return reconstructResponse(entry);
  }

  // 3) new fetch
  const fetchPromise = (async (): Promise<CacheEntry> => {
    console.log(`[cachedFetch] New fetch for ${url}`);
    const response = await fetch(url, normalizedInit);
    const body = await response.text();

    const headers: Record<string, string> = {};
    response.headers.forEach((v, k) => {
      headers[k] = v;
    });

    const entry: CacheEntry = {
      response,
      body,
      status: response.status,
      headers,
      expiresAt: Date.now() + ttlMs,
    };

    // Cache successful responses
    if (response.ok) {
      pruneIfNeeded();
      cache.set(key, entry);
    } else {
      console.warn(`[cachedFetch] Request failed with status ${response.status}:`, url);
    }

    return entry;
  })();

  inflight.set(key, fetchPromise);

  try {
    const entry = await fetchPromise;
    return reconstructResponse(entry);
  } finally {
    inflight.delete(key);
  }
}

export function invalidateCache(urlPrefix?: string): void {
  if (!urlPrefix) {
    cache.clear();
    console.log("[cachedFetch] Cache cleared completely");
    return;
  }
  let count = 0;
  for (const [k] of cache) {
    if (k.startsWith(urlPrefix)) {
      cache.delete(k);
      count++;
    }
  }
  console.log(`[cachedFetch] Invalidated ${count} cache entries with prefix: ${urlPrefix}`);
}

export function getCacheStats(): { cached: number; inflight: number } {
  return {
    cached: cache.size,
    inflight: inflight.size,
  };
}
